import "dexie-export-import";

import Dexie, { type IndexableType, type Table } from "dexie";
import { v4 as uuidv4 } from "uuid";

export interface DebugEvent {
    timestamp: number;
    type: string;
    data: unknown;
}
interface Settings {
    id: "general";
    debug?: boolean;
    sim_score_threshold?: number;
    sim_k_limit?: number;
    stream?: boolean;
    suggestions?: boolean;
    use_agents?: boolean;
    chat_vector_index?: ChatVectorIndex;
}

export enum ChatRole {
    SYSTEM = "system",
    USER = "user",
    ASSISTANT = "assistant",
}

export const SF = "sf";
export const FSR = "fsr";
export const RDS = "rds";
export const PDC_REPORTS = "pdc_reports";
export const PDC_FAILED_SENSOR_REPORTS = "pdc_failed_sensor_reports";
export const ICM = "icm";
export const MAINTENANCE_MANUALS = "maintenance_manuals";
export const MINDMAPS = "mindmaps";
export const DRAWINGS = "drawings";
export const EMAILS = "emails";
export const COMBINED = "combined";
export const SALESORDERS = "salesorders";
export const ADS_GOOD_SOLUTIONS = "ads_good_solutions";
export const USEFUL_INFO = "uinfo";
export const CTS_USER_CHAT = "cts_user_chat";
export const STG_GLOBAL_KNOWLEDGE = "stg_global_knowledge";
export const MGT_FAULT_REPORTS = "mgt_fault_reports";

export type ChatVectorIndex =
    | "salesforce"
    | "cts_knowledge_search_chunkwise"
    | "field_service_reports"
    | "field_service_reports_sg"
    | "combined"
    | "support_emails"
    | "useful_info"
    | "drawings"
    | "salesorders"
    | "rds_reports"
    | "pdc_reports"
    | "pdc_failed_sensor_reports"
    | "ads_good_solutions"
    | "mindmaps"
    | "icm"
    | "maintenance_manuals"
    | "cts_user_chat"
    | "stg_global_knowledge";
export type ChatVectorIndexShorthand =
    | typeof SF
    | typeof FSR
    | typeof COMBINED
    | typeof DRAWINGS
    | typeof EMAILS
    | typeof SALESORDERS
    | typeof USEFUL_INFO
    | typeof RDS
    | typeof MINDMAPS
    | typeof ADS_GOOD_SOLUTIONS
    | typeof PDC_REPORTS
    | typeof PDC_FAILED_SENSOR_REPORTS
    | typeof ICM
    | typeof MAINTENANCE_MANUALS
    | typeof STG_GLOBAL_KNOWLEDGE
    | typeof MGT_FAULT_REPORTS;

export type ChatVectorIndexOption = {
    label: string;
    value: ChatVectorIndex;
    shorthand: ChatVectorIndexShorthand;
};

export class ChatEntity {
    id!: string;
    description!: string;
    totalTokens!: number;
    createdAt!: number;
    updatedAt?: Date;
    totalMessages?: number;
    vectorIndex!: ChatVectorIndex;
    suggestedMessages!: string[];

    static _(vectorIndex: ChatVectorIndex) {
        const record = new ChatEntity();
        record.id = uuidv4();
        record.description = `Chat-${record.id.slice(0, 8)}`;
        record.totalTokens = 0;
        record.createdAt = Date.now();
        record.vectorIndex = vectorIndex;
        record.suggestedMessages = [];

        return record;
    }

    setId(id: string) {
        this.id = id;
        return this;
    }

    setDescription(description: string) {
        this.description = (description || "New Chat").trim();
        return this;
    }

    setTotalTokens(totalTokens: number) {
        this.totalTokens = totalTokens;
        return this;
    }

    add() {
        return db.chats.add(this);
    }
}

interface SuggestionOption {
    text: string;
    score: number;
    freq: number;
}

export type SingleSuggestionOption = SuggestionOption & { oldWord: string };

interface Suggestion {
    text: string;
    oldWord?: string;
    offset: number;
    length: number;
    options: SuggestionOption[];
}

export interface KeySuggestion {
    key: string;
    suggestions: Suggestion[];
}
export interface Reference {
    reference_id: string;
    title: string;
    source: string;
    dataSourceType: string;
}

// This is the format of the reference that we get from the API.
export interface ReferenceJson extends Omit<Reference, "dataSourceType"> {
    data_source_type: string;
}

export class MessageEntity {
    id!: IndexableType;
    toolCall = false;
    chatId!: IndexableType;
    responseId!: string;
    content!: string;
    pretrained!: string;
    done?: boolean;
    debug?: DebugEvent[];
    hasError?: boolean;
    error?: string;
    suggestions?: SingleSuggestionOption[];
    role!: ChatRole;
    createdAt!: number;
    repliedId!: IndexableType;
    references: Reference[] = [];

    static _() {
        const record = new MessageEntity();
        record.id = uuidv4();
        record.createdAt = Date.now();
        return record;
    }

    setId(id: IndexableType) {
        this.id = id;
        return this;
    }

    setResponseId(responseId: string) {
        this.responseId = responseId;
        return this;
    }

    setChatId(id: IndexableType) {
        this.chatId = id;
        return this;
    }

    setContent(content?: string) {
        this.content = (content ?? "unknown response").trim();
        return this;
    }

    setRole(role: ChatRole) {
        this.role = role;
        return this;
    }

    setRepliedId(repliedId: IndexableType) {
        this.repliedId = repliedId;
        return this;
    }

    setIsLoading() {
        this.done = false;
        return this;
    }

    toJSON(): Omit<
        MessageEntity,
        | "setId"
        | "setResponseId"
        | "setChatId"
        | "setContent"
        | "setRole"
        | "setRepliedId"
        | "setIsLoading"
        | "toJSON"
        | "add"
    > {
        return {
            id: this.id,
            toolCall: this.toolCall,
            chatId: this.chatId,
            responseId: this.responseId,
            content: this.content,
            pretrained: this.pretrained,
            done: this.done,
            debug: this.debug,
            hasError: this.hasError,
            error: this.error,
            suggestions: this.suggestions,
            role: this.role,
            createdAt: this.createdAt,
            repliedId: this.repliedId,
            references: this.references,
        };
    }

    add() {
        return db.messages.add(this.toJSON() as MessageEntity);
    }
}

class Database extends Dexie {
    chats!: Table<ChatEntity>;
    messages!: Table<MessageEntity>;
    settings!: Table<Settings>;

    constructor() {
        super("CTS-KS-7.2");
        this.version(8).stores({
            chats: "id, createdAt",
            messages: "id, chatId, responseId, createdAt",
            settings: "id",
        });

        this.on("populate", async () => {
            db.settings.add({
                id: "general",
            });
        });
    }
}
export const db = new Database();

export const updateSettings = <T extends keyof Omit<Settings, "id">>(
    key: T,
    value: Settings[T],
) => {
    db.settings.where({ id: "general" }).modify((settings) => {
        // biome-ignore lint/suspicious/noExplicitAny: Dynamic key assignment requires any
        (settings as any)[key] = value;
    });
};
